import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogIn, ShieldCheck, Mail } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { COMPANY } from "@/lib/company";

export default function AdminLoginPage() {
  const { admin, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && admin) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Logged in.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] aios-starfield flex items-center justify-center px-6 py-20" data-testid="admin-login-page">
      <div className="absolute inset-0 aios-grid-overlay opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src={COMPANY.logoUrl}
            onError={(e) => { e.currentTarget.src = COMPANY.logoFallback; }}
            alt={COMPANY.shortName}
            className="h-24 w-24 object-contain drop-shadow-[0_0_25px_rgba(220,38,38,0.4)]"
          />
          <span className="mt-4 text-red-500 font-display text-xs tracking-[0.3em] font-bold">
            A.I.O · ADMIN PORTAL
          </span>
          <h1 className="font-display text-3xl font-extrabold text-white mt-2 tracking-tighter">
            Sourcing Desk Login
          </h1>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="aios-card p-8 lg:p-10 space-y-5"
        >
          <div className="aios-card-accent" />

          <div>
            <label className="font-display text-xs tracking-widest font-bold text-slate-300 uppercase mb-2 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-red-500" />
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
              placeholder="you@allinonesourcing.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="font-display text-xs tracking-widest font-bold text-slate-300 uppercase mb-2 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-red-500" />
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-[#020617] border border-slate-700 focus:border-red-600 focus:outline-none px-4 py-3 text-slate-100"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="aios-btn-primary w-full text-sm"
          >
            <LogIn className="w-4 h-4" />
            {busy ? "Signing in..." : "Sign In"}
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-2">
            <ShieldCheck className="w-3.5 h-3.5 text-red-500" />
            <span>Encrypted · JWT-secured · Admin only</span>
          </div>
        </form>

        <p className="mt-6 text-center text-slate-500 text-xs">
          Default credentials are set via backend env. Change them from the admin panel after first login.
        </p>
      </div>
    </div>
  );
}
